<template>
  <div>
    <v-stepper-step :complete="currentStep > 1" step="1">
      <h3>WHERE IS YOUR ITEM GOING?</h3>
      <div v-show="currentStep == 1" class="text-caption">
        <p>
          <span class="red--text"><strong>* </strong></span>= Mandatory field
        </p>
      </div>
    </v-stepper-step>
    <v-stepper-content step="1">
      <v-form v-model="recipientFormValid">
        <fieldset class="sr-only-fieldset">
          <legend class="sr-only">Where is your item going?</legend>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                class="hasValidationErrorIcon"
                prepend-inner-icon="mdi-information-outline"
                v-model="recipient.name"
                :rules="nameRules"
                :counter="14"
                placeholder="First Name"
                required
                aria-required="true"
                :aria-describedby="'recipient-name-counter'"
              >
                <template #label>
                  <h4 class="black--text">
                    <span class="red--text"><strong>* </strong></span>FIRST NAME
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                  </h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-name-counter" class="sr-only">
                    {{ recipient.name ? recipient.name.length : 0 }} of 14 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                class="hasValidationErrorIcon"
                prepend-inner-icon="mdi-information-outline"
                v-model="recipient.surname"
                :rules="lastNameRules"
                :counter="20"
                label="Last Name"
                placeholder="Last Name"
                required
                aria-required="true"
                :aria-describedby="'recipient-surname-counter'"
              >
                <template #label>
                  <h4 class="black--text">
                    <span class="red--text"><strong>* </strong></span>LAST NAME
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                  </h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-surname-counter" class="sr-only">
                    {{ recipient.surname ? recipient.surname.length : 0 }} of 20 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <v-text-field
                class="hasValidationErrorIcon"
                prepend-inner-icon="mdi-information-outline"
                v-model="recipient.addressLine1"
                :rules="addressLine1Rules"
                label="Address Line 1"
                placeholder="Address Line 1"
                :counter="50"
                required
                aria-required="true"
                :aria-describedby="'recipient-address-1'"
              >
                <template #label>
                  <h4 class="black--text">
                    <span class="red--text"><strong>* </strong></span>ADDRESS LINE 1
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                  </h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-address-1" class="sr-only">
                    {{ recipient.addressLine1 ? recipient.addressLine1.length : 0 }} of 50 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="12">
              <v-text-field
                v-model="recipient.addressLine2"
                label="Address Line 2"
                placeholder="Address Line 2"
                :counter="50"
                :rules="addressLine2Rules"
                :aria-describedby="'recipient-address-2'"
              >
                <template #label>
                  <h4 class="black--text">ADDRESS LINE 2</h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-address-2" class="sr-only">
                    {{ recipient.addressLine2 ? recipient.addressLine2.length : 0 }} of 50 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                class="hasValidationErrorIcon postCode"
                prepend-inner-icon="mdi-information-outline"
                v-model="recipient.postcode"
                :rules="genericPostcodeRules"
                label="Post Code"
                placeholder="Post Code"
                :counter="17"
                required
                aria-required="true"
                :aria-describedby="'recipient-post-code'"
              >
                <template #label>
                  <h4 class="black--text">
                    <span class="red--text"><strong>* </strong></span>POST CODE
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                  </h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-post-code" class="sr-only">
                    {{ recipient.postcode ? recipient.postcode.length : 0 }} of 17 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                class="hasValidationErrorIcon"
                prepend-inner-icon="mdi-information-outline"
                v-model="recipient.locality"
                :rules="genericLocalityRules"
                :counter="35"
                label="Locality"
                placeholder="Locality"
                required
                aria-required="true"
                :aria-describedby="'recipient-locality'"
              >
                <template #label>
                  <h4 class="black--text">
                    <span class="red--text"><strong>* </strong></span>LOCALITY
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                  </h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-locality" class="sr-only">
                    {{ recipient.locality ? recipient.locality.length : 0 }} of 35 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                :items="filteredCountries"
                label="Country"
                item-text="name"
                item-value="id"
                placeholder="Country"
                v-model="recipient.countryId"
                :rules="countryRules"
                required
                aria-required="true"
              >
                <template #label>
                  <h4 class="black--text">
                    <span class="red--text"><strong>* </strong></span>COUNTRY
                    <span class="sr-only"> (required)</span> <!-- Screen reader text -->
                  </h4>
                </template>
              </v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                class="hasValidationErrorIcon"
                prepend-inner-icon="mdi-information-outline"
                v-model="recipient.email"
                :rules="emailRules"
                :counter="50"
                label="E-mail"
                placeholder="E-mail of addressee"
                :aria-describedby="'recipient-email'"
              >
                <template #label>
                  <h4 class="black--text">E-MAIL</h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-email" class="sr-only">
                    {{ recipient.email ? recipient.email.length : 0 }} of 50 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="recipient.phone"
                :rules="phoneRules"
                :counter="20"
                label="Phone"
                placeholder="Phone of addressee"
                :aria-describedby="'recipient-phone'"
              >
                <template #label>
                  <h4 class="black--text">PHONE</h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-phone" class="sr-only">
                    {{ recipient.phone ? recipient.phone.length : 0 }} of 20 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="recipient.vatNumber"
                :rules="vatNumberRecipientRules"
                :counter="20"
                label="VAT Number"
                placeholder="VAT Number of addressee"
                :aria-describedby="'recipient-vat-number'"
              >
                <template #label>
                  <h4 class="black--text">VAT NUMBER</h4>
                </template>

                <!-- Hidden span for screen readers -->
                <template v-slot:append-outer>
                  <span id="recipient-vat-number" class="sr-only">
                    {{ recipient.vatNumber ? recipient.vatNumber.length : 0 }} of 20 characters used
                  </span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </fieldset>
      </v-form>
      <v-btn 
        color="primary" 
        @click="goNext()" 
        :disabled="!recipientFormValid"
        :aria-disabled="!recipientFormValid">
        Next
      </v-btn>

      <div
        class="sr-only"
        aria-live="polite"
        v-if="!recipientFormValid"
      >
        Please complete all required recipient fields to proceed.
      </div>
    </v-stepper-content>
  </div>
</template>

<script>

import validationRules from "../../helpers/ValidationRules";
import customsRules from "../../helpers/CustomsRules";
import { createNamespacedHelpers } from "vuex";
const { mapGetters: mapDeclarationGetters } = createNamespacedHelpers(
  "declarations"
);

export default {
  name: "Step01Recipient",
  data() {
    return {
      recipientFormValid: false,
      recipient: {
        name: "",
        surname: "",
        email: "",
        addressLine1: "",
        addressLine2: "",
        postcode: "",
        locality: "",
        countryId: "",
        phone: "",
        vatNumber: "",
        isRequiresContentItems: false,
      },
      ...validationRules,
    };
  },
  computed: {
    
    ...mapDeclarationGetters(["countries"]),

    filteredCountries() {
      return this.countries.filter(c => c.name?.toLowerCase() !== "malta");
    },
    currentStep: {
      set(currentStep) {
        this.$store.dispatch("declarations/setCurrentStep", currentStep);
      },
      get() {
        return this.$store.getters["declarations/currentStep"];
      },
    },
  },
  methods: {
    goNext() {
      if (!this.recipient.email && !this.recipient.phone){
        this.$store.dispatch("declarations/showRequiredEmailOrPhoneError");
      }
      else {
        const countryId = this.recipient.countryId;
        const {
          stepNumber: nextStep,
          isRequiresContentItems,
        } = customsRules.afterRecipientGoTo(countryId, this.countries);

        this.recipient.isRequiresContentItems = isRequiresContentItems;

        this.$store.dispatch(
          "declarations/Step01Recipient/setRecipient",
          this.recipient
        );

        this.$store.dispatch("declarations/setCurrentStep", nextStep);
      }
    },
  },
  mounted() {},
};
</script>